import { advancedFilterModelToGridFilterModel } from '@nohara-dxdev/react-core';
import { filterModelToRSQL, rsqlBuilder, serializeRSQL } from '@nohara-dxdev/rsql';

import type { ItemTemplateFilterValues } from '../providers/item-template-filter-values-provider';
import type { CustomExpressionNode } from '@nohara-dxdev/rsql';

// Re-export the interface for convenience
export type { ItemTemplateFilterValues };

/**
 * Convert ItemTemplateFilterValues to API query parameters
 *
 * This function:
 * 1. Converts filterModel/advancedFilterModel to RSQL string
 * 2. Maps to backend API contract
 */
export function convertItemTemplateFilterToApiParams(
  filterValues: ItemTemplateFilterValues,
): Record<string, unknown> {
  const params: Record<string, unknown> = {};

  // Build RSQL expressions to combine
  const rsqlExpressions: CustomExpressionNode[] = [];

  // Prioritize advancedFilterModel over filterModel if both are present
  if (filterValues.advancedFilterModel && filterValues.advancedFilterModel.conditions.length > 0) {
    // Handle advancedFilterModel (converts to filterModel format)
    try {
      const gridFilterModel = advancedFilterModelToGridFilterModel(
        filterValues.advancedFilterModel,
      );
      if (gridFilterModel.items.length > 0) {
        const filterModelAST = filterModelToRSQL([
          gridFilterModel as import('@mui/x-data-grid').GridFilterModel,
        ]);
        if (filterModelAST) {
          rsqlExpressions.push(filterModelAST);
        }
      }
    } catch (error) {
      console.error('Failed to convert advancedFilterModel to RSQL AST:', error);
    }
  } else if (filterValues.filterModel && filterValues.filterModel.length > 0) {
    // Convert filterModel array to RSQL AST (fallback for backward compatibility)
    try {
      const filterModelAST = filterModelToRSQL(filterValues.filterModel);
      if (filterModelAST) {
        rsqlExpressions.push(filterModelAST);
      }
    } catch (error) {
      console.error('Failed to convert filterModel array to RSQL AST:', error);
    }
  }

  // Combine all RSQL expressions into a single RSQL string
  let rsqlFilter: string | undefined;
  if (rsqlExpressions.length > 0) {
    if (rsqlExpressions.length === 1) {
      rsqlFilter = serializeRSQL(rsqlExpressions[0]);
    } else {
      rsqlFilter = serializeRSQL(rsqlBuilder.and(...rsqlExpressions));
    }
  }

  // Add RSQL filter if present
  if (rsqlFilter) {
    params.rsqlFilter = rsqlFilter;
  }

  // Add sorting params
  if (filterValues.sortField) {
    params.sortField = filterValues.sortField;
  }
  if (filterValues.sortOrder) {
    params.sortOrder = filterValues.sortOrder;
  }

  // Add nameOrTemplateKeyMatch param (full-text search)
  if (filterValues.nameOrTemplateKeyMatch) {
    params.nameOrTemplateKeyMatch = filterValues.nameOrTemplateKeyMatch;
  }

  return params;
}
